import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, Download, Search, UserX, UserCheck, BarChart3, AlertTriangle, RefreshCw, CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const StatCard = ({ icon: Icon, label, value, loading }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      <Icon className="w-4 h-4 text-[#1A6B3C]" />
    </CardHeader>
    <CardContent>
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <div className="text-2xl font-bold">{value?.toLocaleString() ?? 0}</div>
      )}
    </CardContent>
  </Card>
);

const UsersPage = () => {
  const [userStats, setUserStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailStats, setUserDetailStats] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/users/stats`),
        axios.get(`${API}/users`, { params: { search, page, limit: 20 } })
      ]);
      setUserStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTotalPages(usersRes.data.totalPages);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeactivate = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/deactivate`);
      fetchUsers();
    } catch (error) {
      console.error("Failed to deactivate user:", error);
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/activate`);
      fetchUsers();
    } catch (error) {
      console.error("Failed to activate user:", error);
    }
  };

  const handleViewStats = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/stats`);
      setUserDetailStats(response.data);
      setSelectedUser(userId);
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
    }
  };

  const handleExport = () => {
    window.open(`${API}/users/export`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Users</h2>
          <p className="text-muted-foreground">Manage registered CarbiQ users</p>
        </div>
        <Button onClick={handleExport} variant="outline" data-testid="export-users-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Privacy Notice */}
      <Alert className="bg-amber-500/10 border-amber-500/50">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertDescription className="text-amber-700 dark:text-amber-300">
          User health data (glucose readings, medication logs) is NOT accessible from this interface. 
          Only aggregate statistics are shown. GDPR compliant.
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total Users" value={userStats?.totalUsers} loading={loading} />
        <StatCard icon={CheckCircle} label="Active (30 days)" value={userStats?.activeUsers} loading={loading} />
        <StatCard icon={RefreshCw} label="Sync Enabled" value={userStats?.syncEnabledUsers} loading={loading} />
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search by email..." 
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="user-search-input"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead>Sync</TableHead>
              <TableHead>Readings</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-mono text-sm">{user.maskedEmail}</TableCell>
                  <TableCell className="text-sm">{new Date(user.joinDate).toLocaleDateString()}</TableCell>
                  <TableCell className="text-sm">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {user.syncEnabled ? (
                      <Badge className="bg-green-500/20 text-green-600">Enabled</Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell>{user.readingsCount}</TableCell>
                  <TableCell>
                    {user.isActive ? (
                      <Badge className="bg-green-500/20 text-green-600">Active</Badge>
                    ) : (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {user.isActive ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeactivate(user.id)}
                                data-testid={`deactivate-${user.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Deactivate User</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleActivate(user.id)}
                                data-testid={`activate-${user.id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Activate User</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleViewStats(user.id)}
                              data-testid={`view-stats-${user.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>View Stats</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* User Stats Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetailStats(null); }}>
        <DialogContent data-testid="user-stats-dialog">
          <DialogHeader>
            <DialogTitle>User Statistics</DialogTitle>
            <DialogDescription>
              Aggregate data only - no personal health information shown
            </DialogDescription>
          </DialogHeader>
          {userDetailStats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-mono">{userDetailStats.maskedEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={userDetailStats.isActive ? "default" : "destructive"}>
                    {userDetailStats.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Join Date</p>
                  <p>{new Date(userDetailStats.joinDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sync Enabled</p>
                  <p>{userDetailStats.syncEnabled ? 'Yes' : 'No'}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Data Statistics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{userDetailStats.stats.glucoseReadings}</p>
                    <p className="text-sm text-muted-foreground">Glucose Readings</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{userDetailStats.stats.medicationLogs}</p>
                    <p className="text-sm text-muted-foreground">Medication Logs</p>
                  </div>
                </div>
                {userDetailStats.stats.firstReadingDate && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Data range: {new Date(userDetailStats.stats.firstReadingDate).toLocaleDateString()} - {new Date(userDetailStats.stats.lastReadingDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
